import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';
import { useAuthStore } from '../store/authStore';

interface CommentReactionsProps {
  photoId: string;
  commentId: string;
}

interface Reaction {
  commentId: string;
  userId: string;
  userName: string;
  userEmail: string;
  emoji: string;
  createdAt: string;
}

interface ReactionCounts {
  [emoji: string]: {
    count: number;
    users: Array<{ userId: string; userName: string }>;
  };
}

const ALLOWED_EMOJIS = ['❤️', '👍', '😂', '🎉', '👏', '🔥'];

export default function CommentReactions({ photoId, commentId }: CommentReactionsProps) {
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  // Fetch reactions for this comment with real-time updates
  const { data: reactionsData } = useQuery({
    queryKey: ['reactions', commentId],
    queryFn: () => api.listReactions(photoId, commentId),
    refetchInterval: 15 * 1000, // Poll every 15 seconds for reaction updates
    staleTime: 0,
  });

  const reactions: Reaction[] = reactionsData?.reactions || [];
  const reactionCounts: ReactionCounts = reactionsData?.reactionCounts || {};

  // Find user's current reaction (if any)
  const userReaction = reactions.find(r => r.userId === user?.userId);

  // Add/update reaction mutation
  const addReactionMutation = useMutation({
    mutationFn: (emoji: string) => api.addReaction(photoId, commentId, emoji),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reactions', commentId] });
      setShowEmojiPicker(false);
    },
  });

  // Remove reaction mutation
  const removeReactionMutation = useMutation({
    mutationFn: () => api.removeReaction(photoId, commentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reactions', commentId] });
    },
  });

  const handleEmojiClick = (emoji: string) => {
    if (userReaction?.emoji === emoji) {
      // User clicked the same emoji - remove reaction
      removeReactionMutation.mutate();
    } else {
      // User clicked a different emoji or no reaction yet - add/update reaction
      addReactionMutation.mutate(emoji);
    }
  };

  const handleAddClick = () => {
    setShowEmojiPicker(!showEmojiPicker);
  };

  // Get tooltip text for who reacted
  const getReactionTooltip = (emoji: string): string => {
    const data = reactionCounts[emoji];
    if (!data || data.users.length === 0) return '';
    
    const names = data.users.map(u => u.userName);
    if (names.length === 1) return names[0];
    if (names.length === 2) return `${names[0]} and ${names[1]}`;
    if (names.length === 3) return `${names[0]}, ${names[1]}, and ${names[2]}`;
    return `${names[0]}, ${names[1]}, and ${names.length - 2} others`;
  };

  return (
    <div className="flex items-center gap-1 mt-2 flex-wrap">
      {/* Show reaction counts for each emoji that has reactions */}
      {Object.entries(reactionCounts).map(([emoji, data]) => {
        const isUserReaction = userReaction?.emoji === emoji;
        return (
          <button
            key={emoji}
            onClick={() => handleEmojiClick(emoji)}
            title={getReactionTooltip(emoji)}
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs transition-all hover:scale-110 ${
              isUserReaction
                ? 'bg-blue-100 border-2 border-blue-400'
                : 'bg-gray-100 border border-gray-300 hover:bg-gray-200'
            }`}
          >
            <span>{emoji}</span>
            <span className={`font-medium ${isUserReaction ? 'text-blue-900' : 'text-gray-700'}`}>
              {data.count}
            </span>
          </button>
        );
      })}

      {/* Add reaction button */}
      <div className="relative">
        <button
          onClick={handleAddClick}
          className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gray-100 hover:bg-gray-200 border border-gray-300 transition-all hover:scale-110"
          title="Add reaction"
        >
          <span className="text-xs">+</span>
        </button>

        {/* Emoji picker popup */}
        {showEmojiPicker && (
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 z-[60]"
              onClick={() => setShowEmojiPicker(false)}
            />
            {/* Picker */}
            <div className="absolute left-0 top-full mt-2 bg-white rounded-xl shadow-2xl border border-gray-300 p-4 grid grid-cols-2 sm:grid-cols-3 gap-3 z-[70] min-w-[160px] sm:min-w-[200px]">
              {ALLOWED_EMOJIS.map((emoji) => {
                const isSelected = userReaction?.emoji === emoji;
                return (
                  <button
                    key={emoji}
                    onClick={() => handleEmojiClick(emoji)}
                    className={`w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center text-3xl sm:text-4xl hover:bg-gray-100 rounded-lg transition-all duration-100 active:scale-95 hover:scale-110 ${
                      isSelected ? 'bg-blue-100 ring-2 ring-blue-500' : ''
                    }`}
                    title={isSelected ? 'Remove reaction' : 'Add reaction'}
                  >
                    {emoji}
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

